import { getHostName } from '@nike/ciclp-config';
import {
  includes,
  head,
  isEmpty,
  defaultTo,
  filter,
  complement,
  both,
  pipe,
  chain,
  toPairs,
  whereEq,
} from 'ramda';
import { deepEquals } from '@nike/ciclp-utils';
import {
  CARD_ACTION_DESTINATION_TYPES,
  CARD_ACTION_TYPES,
  PDP_URL_TYPES,
} from '../../../constants';
import { extractUrlIngredients } from '../utils';

const isObject = x => x !== null && typeof x === 'object';

const pdpUrlTypeCheckers = {
  [PDP_URL_TYPES.NIKEID]: x => x === 'NIKEID',
  [PDP_URL_TYPES.NIKEDOTCOM]: includes('Nike.com'),
  [PDP_URL_TYPES.SNKRS]: includes('SNKRS'),
};

export const pickUrlByIngredients = (urls = [], urlIngredients = {}) => {
  const pickedIngredients =
    extractUrlIngredients(urlIngredients).urlIngredients ?? {};
  const urlEntry = urls?.find(
    pipe(head, x => deepEquals(x, pickedIngredients)),
  );
  return urlEntry ? urlEntry.at(-1) : null;
};

export const pickBuyingToolFromList = (
  buyingToolsList,
  buyingToolsIngredients = {},
) => buyingToolsList?.find(whereEq(buyingToolsIngredients));

export const getPDPURL = {
  [PDP_URL_TYPES.NIKEID]: ({
    urlIngredients = {},
    languageCountryPath,
    urls = [],
  }) => {
    const { slug = '', piid = '', pbid = '' } = urlIngredients;
    return (
      pickUrlByIngredients(urls, urlIngredients) ||
      `https://${getHostName()}/${
        languageCountryPath ?? ''
      }u/${slug}-${piid}/${pbid}`
    );
  },
  [PDP_URL_TYPES.NIKEDOTCOM]: ({
    urlIngredients = {},
    languageCountryPath,
    urls = [],
  }) => {
    const { styleColor = '', slug = '' } = urlIngredients;

    return (
      pickUrlByIngredients(urls, urlIngredients) ||
      `https://${getHostName()}/${
        languageCountryPath ?? ''
      }t/${slug}/${styleColor}`
    );
  },
  [PDP_URL_TYPES.SNKRS]: ({
    languageCountryPath = '',
    urlIngredients = {},
  }) => {
    const { slug = '' } = urlIngredients;
    return `https://${getHostName()}/${
      languageCountryPath ?? ''
    }launch/t/${slug}`;
  },
};

const PDP_LINK_INGREDIENTS = {
  [PDP_URL_TYPES.NIKEID]: ({ slug = '', piid = '', pbid = '' } = {}) => ({
    pbid,
    piid,
    slug,
  }),
  [PDP_URL_TYPES.NIKEDOTCOM]: ({ styleColor, slug } = {}) => ({
    slug,
    styleColor,
    type: 'PDP',
  }),
  [PDP_URL_TYPES.SNKRS]: ({ slug, styleColor } = {}) => ({
    slug,
    styleColor,
  }),
  [PDP_URL_TYPES.NIKEDOTCOM_LEGACY]: ({
    slug = '',
    pid = '',
    productGroupId = '',
  } = {}) => ({
    pid,
    productGroupId,
    slug,
  }),
};

export const getPDPUrl = ({ pdpType, ...params }) =>
  getPDPURL?.[pdpType](params);

export const getPDPIngredients = ({ pdpType, ...params }) =>
  PDP_LINK_INGREDIENTS?.[pdpType](params);

export const getPDPType = (styleType, channelInfo) =>
  pipe(
    toPairs,
    arr =>
      arr.find(([, checker]) =>
        checker((styleType === 'NIKEID' ? styleType : channelInfo) || ''),
      ),
    pair => (pair ? pair[0] : PDP_URL_TYPES.NIKEDOTCOM),
  )(pdpUrlTypeCheckers);

export const getUrlIngredients = ({ destination = {} } = {}) =>
  extractUrlIngredients(destination).urlIngredients ?? {};

/**
 *    @description extract all URL ingredients from card and returns them as array of urlIngredients
 *    @param {Object[]} arr list of objects with url ingredients
 *    @returns {Object[]} urls array with all url ingredients object
 *    extractIngredientsFromList: arr => urls,
 * */
export const extractIngredientsFromList = pipe(
  defaultTo([]),
  chain(({ urlIngredients, actions }) => [
    urlIngredients,
    ...((actions && extractIngredientsFromList(actions)) ?? []),
  ]),
  filter(both(complement(isEmpty), isObject)),
);

/**
 *    @description replaces link urlIngredients with url
 *    @param {Object} link object of link
 *    @param {Object} urlIngredients object with ingredients that matches to some URL from the list
 *    @param {[Object, string][]} urls list of generated URLs
 *    @param {Object} property name of property for inserted link
 *    @returns {Object} linkWithUrl prepared data needed on the page
 *    mergeLinkWithIngredients: ({ urlIngredients, ...link }, urls, property) => linkWithUrl,
 * */
export const mergeLinkWithIngredients = ({
  action: {
    urlIngredients = {},
    buyingToolsIngredients = {},
    actions,
    ...link
  } = {},
  urls,
  property = 'cardLinkUrl',
  buyingToolsList = [],
}) => {
  let url = pickUrlByIngredients(urls, urlIngredients) || link[property];

  if (actions) {
    link.actions = actions.map(a =>
      mergeLinkWithIngredients({ action: a, buyingToolsList, property, urls }),
    );
  }

  if (url && link.destinationType === CARD_ACTION_DESTINATION_TYPES.POPUP) {
    url = url
      .replace('[country]', link.countryCode)
      .replace('[language]', link.language);
  }

  if (link.actionType === CARD_ACTION_TYPES.BUYING_TOOLS) {
    return {
      ...link,
      buyingToolsIngredients: pickBuyingToolFromList(
        buyingToolsList,
        buyingToolsIngredients,
      ),
    };
  }
  return { ...link, [property]: url };
};
